/* eslint-disable no-console */
import isEqual from 'lodash.isequal';
import { compare } from 'compare-versions';

import * as api from 'service/InstallerApp';
import {
  addError,
  addInfo,
  addSuccess,
  dismissAll,
  addWarning,
} from 'rdx/alert';
import {
  getAvailableMSes,
  getMSDataByEntity,
  getInstallerVersion,
} from 'rdx/hardware/installer/selectors';

import * as types from './types';

export const startInstallerLoader = () => ({
  type: types.INSTALLER_LOADER.START,
});

export const stopInstallerLoader = () => ({
  type: types.INSTALLER_LOADER.STOP,
});

const alertOptions = { dismissible: false, selfDismiss: false };

/**
 * Installer App - get version and currently insatlled micro-services
 *
 * @param options {{}}
 * @param options.withAlert {Boolean} - if there is an error it will be shown as alert
 */
export const fetchInstallerInfo =
  (options = { withAlert: true }) =>
  async (dispatch) => {
    dispatch({ type: types.FETCH_INSTALLER_INFO.START });
    try {
      const data = await api.getInfo();
      dispatch({
        type: types.FETCH_INSTALLER_INFO.SUCCESS,
        payload: data.data,
      });
    } catch (err) {
      dispatch({
        type: types.FETCH_INSTALLER_INFO.ERROR,
        payload: err.message,
      });

      if (options.withAlert) {
        dispatch(
          addError('Unable to connect to the Installer App.', {
            selfDismiss: 3500,
            dismissible: true,
          }),
        );
      } else {
        throw err;
      }
    }
  };

/**
 * Installer App - update installer to latest version
 */
export const updateInstaller = () => async (dispatch) => {
  dispatch(startInstallerLoader());
  dispatch(
    addInfo('Updating Installer App...', {
      dismissible: false,
      selfDismiss: false,
    }),
  );
  try {
    const response = await api.updateInstaller();
    dispatch({ type: types.UPDATE_INSTALLER.SUCCESS });
    dispatch(dismissAll());
    dispatch(
      addSuccess(response?.message ?? 'Installer updated successfully', {
        selfDismiss: true,
        dismissible: true,
      }),
    );
    dispatch({ type: types.INSTALLER_LOADER.STOP });
  } catch (err) {
    dispatch({ type: types.UPDATE_INSTALLER.ERROR });
    dispatch(stopInstallerLoader());
    dispatch(dismissAll());
    dispatch(
      addError('Installer updated failed', {
        selfDismiss: true,
        dismissible: true,
      }),
    );
  }
};
/**
 * Installer App - fetch all available versions for a micro-service
 * @param entity - micro-service name
 */
export const fetchMSVersions = (entity) => async (dispatch) => {
  dispatch({ type: types.FETCH_MS_VERSIONS.START });
  try {
    const { data } = await api.getIntegrationVersion(entity);
    const payload = {
      entity,
      builds: data?.builds || [],
    };
    dispatch({ type: types.FETCH_MS_VERSIONS.SUCCESS, payload });
  } catch (err) {
    console.error(err);
    dispatch({ type: types.FETCH_MS_VERSIONS.ERROR, payload: err.message });
  }
};

/**
 * Installer App - fetch all available micro-services
 */
export const fetchAvailableMS = () => async (dispatch) => {
  dispatch({ type: types.FETCH_AVAILABLE_MS.START });
  try {
    const { data } = await api.getAvailableIntegrations();
    const payload = data?.builds || [];
    dispatch({ type: types.FETCH_AVAILABLE_MS.SUCCESS, payload });
  } catch (err) {
    dispatch({ type: types.FETCH_AVAILABLE_MS.ERROR, payload: err.message });
  }
};

export const setIsProduction = (payload) => ({
  type: types.SET_IS_PRODUCTION,
  payload,
});

/**
 * Installer App - fetch info, fetch available ms, fetch versions for each available ms
 */
export const initialInstallerFetch = () => async (dispatch, getState) => {
  dispatch({ type: types.INSTALLER_LOADER.START });
  try {
    await dispatch(fetchInstallerInfo({ withAlert: false }));
    await dispatch(fetchAvailableMS());
    const availableMS = getAvailableMSes(getState());
    await availableMS.reduce(
      (pr, entity) => pr.then(() => dispatch(fetchMSVersions(entity))),
      Promise.resolve(),
    );
    dispatch({ type: types.INSTALLER_LOADER.STOP });
  } catch (err) {
    dispatch({ type: types.INSTALLER_LOADER.STOP });
    console.error(err);
  }
};

/**
 * Installer App  - download and install micro-service
 *
 * @param {InstallerParams} params - entity - MS name, version - MS version
 */

export const installMS =
  ({ entity, version }) =>
  async (dispatch) => {
    dispatch({ type: types.INSTALLER_INSTALL_MS.START });
    let processStage = null;
    try {
      dispatch(addInfo('Downloading...', alertOptions));
      processStage = 'download';
      await api.downloadIntegration(entity, version);
      dispatch(dismissAll());
      dispatch(addInfo('Installing...', alertOptions));
      processStage = 'install';
      await api.installIntegration(entity, version);
      dispatch({ type: types.INSTALLER_INSTALL_MS.SUCCESS });
      dispatch(dismissAll());
      dispatch(addSuccess('Installed Successfully'));
      processStage = 're-fetch micro-services';
      await dispatch(fetchInstallerInfo({ withAlert: false }));
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_INSTALL_MS.ERROR,
        payload: err.message,
      });
      dispatch(dismissAll());
      dispatch(addInfo(`Failed to ${processStage}. Error: ${err.message}`));
    }
  };

/**
 * Installer App  - start micro-service
 *
 * @param {InstallerParams} params
 */
export const startMS =
  ({ entity, version }) =>
  async (dispatch) => {
    dispatch({ type: types.INSTALLER_START_MS.START });
    dispatch(addInfo('Starting...', alertOptions));
    try {
      await api.startIntegrationProcess(entity, version);
      dispatch(dismissAll());
      dispatch(addSuccess('Started Successfully'));
      dispatch({
        type: types.INSTALLER_START_MS.SUCCESS,
        payload: { entity, version, status: 'StatusRunning' },
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_START_MS.ERROR,
        payload: err.message,
      });
      dispatch(dismissAll());
      dispatch(addError(`Failed to start. Error: ${err.message}`));
    }
  };

/**
 * Installer App  - stop micro-service
 *
 * @param {InstallerParams} params
 */
export const stopMS =
  ({ entity, version }) =>
  async (dispatch) => {
    dispatch({ type: types.INSTALLER_STOP_MS.START });
    dispatch(addInfo('Stopping...', alertOptions));
    try {
      await api.stopIntegrationProcess(entity, version);
      dispatch(dismissAll());
      dispatch(addSuccess('Stopped Successfully'));
      dispatch({
        type: types.INSTALLER_STOP_MS.SUCCESS,
        payload: { entity, version, status: 'StatusStopped' },
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_STOP_MS.ERROR,
        payload: err.message,
      });
      dispatch(dismissAll());
      dispatch(addError(`Failed to stop. Error: ${err.message}`));
    }
  };

/**
 * Installer App - remove micro-service
 * @param entity {string} - micro-service name
 * @param version {sting} - micro-service version
 */
export const removeMS =
  ({ entity, version }) =>
  async (dispatch) => {
    dispatch({ type: types.INSTALLER_REMOVE_MS.START });
    dispatch(addInfo('Removing...', alertOptions));
    try {
      await api.removeIntegration(entity, version);
      dispatch({
        type: types.INSTALLER_REMOVE_MS.SUCCESS,
        payload: { entity, version },
      });
      dispatch(dismissAll());
      dispatch(addSuccess('Removed Successfully'));
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_REMOVE_MS.ERROR,
        payload: err.message,
      });
      dispatch(addError(`Failed to remove. Error: ${err.message}`));
    }
  };

/**
 * Installer App - update micro-service to given version
 * @param entity {string} - micro-service name
 * @param version {sting} - micro-service version
 */
export const updateMSVersion =
  ({ entity, version }) =>
  async (dispatch, getState) => {
    if (compare(getInstallerVersion(getState()), 'v2.0.4', '<')) {
      dispatch(
        addWarning(
          'This feature is only available for installer v2.0.4 and above. Please, update Installer.',
        ),
      );
      return;
    }

    const { installedVersion } = getMSDataByEntity(entity)(getState());
    if (installedVersion === version) {
      dispatch(addWarning(`Version ${version} is already installed!`));
      return;
    }

    dispatch({ type: types.INSTALLER_UPDATE_MS_VERSION.START });
    dispatch(addInfo('Updating...', alertOptions));
    try {
      await api.updateIntegrationVersion(entity, version);
      dispatch({
        type: types.INSTALLER_UPDATE_MS_VERSION.SUCCESS,
        payload: { entity, version },
      });
      dispatch(dismissAll());
      dispatch(addSuccess(`Updated Successfully to version: ${version}`));
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_UPDATE_MS_VERSION.ERROR,
        payload: err.message,
      });
      dispatch(dismissAll());
      dispatch(addError(`Failed to update. Error: ${err.message}`));
    }
  };

/**
 * Installer App  - get micro-service config
 *
 * @param {InstallerParams} params
 */
export const fetchMicroServiceConfig =
  ({ entity, version }) =>
  async (dispatch) => {
    dispatch({ type: types.INSTALLER_FETCH_MS_CONFIG.START });
    try {
      await api.getIntegrationConfig(entity, version);
      dispatch({ type: types.INSTALLER_FETCH_MS_CONFIG.SUCCESS });
    } catch (err) {
      console.error(err);
      dispatch({
        type: types.INSTALLER_FETCH_MS_CONFIG.ERROR,
        payload: err.message,
      });
    }
  };

/**
 * Installer App  - set micro-service config
 *
 * @param {InstallerParams & {config: string}} params - config - stringified JSON object
 */
export const setMicroServiceConfig = (params) => async (dispatch) => {
  dispatch({ type: types.INSTALLER_STOP_MS.START });
  try {
    await api.saveIntegrationConfig(params);
    dispatch({ type: types.INSTALLER_STOP_MS.SUCCESS });
  } catch (err) {
    console.error(err);
    dispatch({
      type: types.INSTALLER_STOP_MS.ERROR,
      payload: err.message,
    });
  }
};

/**
 * Set Micro-service endpoint
 *
 * @param entity {string} - MS name
 * @param version {string} - MS version
 * @param endpoint - MS connection endpoint
 * @returns {{payload: {endpoint: *, version: *, entity: *}, type: string}}
 */
export const setMSLocalEndpoint = ({ entity, version, endpoint }) => ({
  type: types.SET_MS_LOCAL_ENDPOINT,
  payload: {
    entity,
    version,
    endpoint,
  },
});

/**
 * Update Micro-service configuration and restart it
 * @param entity {string} - micro-service name
 * @param versionProp {string} - micro-service version
 * @param config {object} - new configuration
 */
export const updateMSConfig =
  ({ entity, version = null, config = null }) =>
  async (dispatch, getState) => {
    const state = getState();
    const { name } = getMSDataByEntity(entity)(state);
    try {
      const { data } = await api.getIntegrationConfig(entity, version);
      const oldMSConfig = JSON.parse(data.config || '{}');
      const newMSConfig = {};
      const keys = Object.keys(oldMSConfig);
      keys.forEach((key) => {
        newMSConfig[key] = config[key] || oldMSConfig[key];
      });

      // Generate integration specific endpoints.
      if (keys.includes('Port')) {
        const endpoint = `https://localhost.erply.com:${newMSConfig.Port}`;
        dispatch(setMSLocalEndpoint({ entity, version, endpoint }));
      }

      if (!isEqual(newMSConfig, oldMSConfig)) {
        await api.saveIntegrationConfig(
          entity,
          version,
          JSON.stringify(newMSConfig),
        );
        await api.stopIntegrationProcess(entity, version);
        await api.startIntegrationProcess(entity, version);
      }
    } catch (err) {
      console.error(err);
      dispatch(
        addError(
          `Failed to update ${name || 'MS'} Config via installer. Error: ${
            err.message
          }`,
        ),
      );
      throw err;
    }
  };
